
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ExternalLink, Activity, AlertTriangle, Users, Calendar, CreditCard, UserPlus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface WebhookConfig {
  id: string;
  name: string;
  eventType: string;
  description: string;
  destinationUrl: string;
  isActive: boolean;
  icon: any;
  method: string;
  contentType: string;
  triggerCondition?: string;
}

const WebhookManagement = () => {
  const { toast } = useToast();

  // Current webhook configurations based on your existing setup
  const webhookConfigs: WebhookConfig[] = [
    {
      id: "new-booking",
      name: "New Booking Webhook",
      eventType: "new_booking",
      description: "Triggered when a new booking is created",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMjA0Mzc1MjY4NTUzMDUxMzUi_pc",
      isActive: true,
      icon: Calendar,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "After booking insert"
    },
    {
      id: "checkin-available",
      name: "Check-in Available Webhook",
      eventType: "checkin_available",
      description: "Triggered 5 minutes before booking start time",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMjA0Mzc1MjY4NTUzMDUxMzUi_pc",
      isActive: true,
      icon: Activity,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "5 minutes before booking"
    },
    {
      id: "new-signup",
      name: "New Signup Webhook",
      eventType: "new_signup",
      description: "Triggered when a new user signs up",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMjA0Mzc1MjY4NTUzMDUxMzUi_pc",
      isActive: true,
      icon: UserPlus,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "After user registration"
    },
    {
      id: "checked-out",
      name: "Checked Out Webhook",
      eventType: "checked_out",
      description: "Triggered when a booking is marked as completed",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMzA0Mzc1MjY0NTUzNTUxMzYi_pc",
      isActive: true,
      icon: Users,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "When booking status = completed"
    },
    {
      id: "membership-purchases",
      name: "Membership Purchase Webhook",
      eventType: "membership_purchase",
      description: "Triggered when a membership is purchased",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMjA0Mzc1MjY4NTUzMDUxMzUi_pc",
      isActive: true,
      icon: CreditCard,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "After successful payment"
    },
    {
      id: "error-webhooks",
      name: "Error Webhook",
      eventType: "payment_error",
      description: "Triggered when payment errors occur",
      destinationUrl: "https://connect.pabbly.com/workflow/sendwebhookdata/IjU3NjYwNTZiMDYzMzA0MzA1MjZiNTUzMjUxMzAi_pc",
      isActive: true,
      icon: AlertTriangle,
      method: "POST",
      contentType: "application/json",
      triggerCondition: "On payment failures"
    }
  ];

  const handleTestWebhook = (webhook: WebhookConfig) => {
    toast({
      title: "Webhook Test",
      description: `Testing ${webhook.name}...`,
    });
    // This would trigger the actual webhook test in a real implementation
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  };

  const formatUrl = (url: string) => {
    if (url.length > 50) {
      return url.substring(0, 47) + "...";
    }
    return url;
  };

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Webhook Management</h2>
            <p className="text-gray-600 mt-1">Monitor and manage your webhook integrations</p>
          </div>
          <div className="flex items-center space-x-2">
            <Badge variant="outline" className="bg-blue-50 text-blue-700">
              {webhookConfigs.filter(w => w.isActive).length} Active
            </Badge>
            <Badge variant="outline" className="bg-gray-50 text-gray-700">
              {webhookConfigs.length} Total
            </Badge>
          </div>
        </div>

        {/* Webhook Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {webhookConfigs.map((webhook) => {
            const IconComponent = webhook.icon;
            return (
              <Card key={webhook.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${webhook.isActive ? 'bg-blue-100' : 'bg-gray-100'}`}>
                        <IconComponent className={`h-5 w-5 ${webhook.isActive ? 'text-blue-600' : 'text-gray-500'}`} />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{webhook.name}</CardTitle>
                        <p className="text-sm text-gray-500 mt-1">{webhook.description}</p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(webhook.isActive)}>
                      {webhook.isActive ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                </CardHeader>
                
                <CardContent className="space-y-4">
                  {/* Event Type */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Event Type</label>
                    <p className="text-sm font-mono bg-gray-50 px-2 py-1 rounded mt-1">{webhook.eventType}</p>
                  </div>

                  {/* Destination URL */}
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Destination URL</label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <p className="text-sm font-mono bg-gray-50 px-2 py-1 rounded flex-1 cursor-help">
                            {formatUrl(webhook.destinationUrl)}
                          </p>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-md break-all">
                          <p>{webhook.destinationUrl}</p>
                        </TooltipContent>
                      </Tooltip>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(webhook.destinationUrl, '_blank')}
                        className="p-1 h-8 w-8"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  {/* Technical Details */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Method</label>
                      <p className="text-sm bg-gray-50 px-2 py-1 rounded mt-1">{webhook.method}</p>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Content Type</label>
                      <p className="text-sm bg-gray-50 px-2 py-1 rounded mt-1">{webhook.contentType}</p>
                    </div>
                  </div>

                  {/* Trigger Condition */}
                  {webhook.triggerCondition && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Trigger Condition</label>
                      <p className="text-sm text-gray-700 mt-1">{webhook.triggerCondition}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-2 pt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestWebhook(webhook)}
                      className="flex-1"
                    >
                      Test Webhook
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Summary Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Webhook Integration Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{webhookConfigs.filter(w => w.isActive).length}</div>
                <div className="text-sm text-gray-500">Active Webhooks</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">2</div>
                <div className="text-sm text-gray-500">Pabbly Endpoints</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">6</div>
                <div className="text-sm text-gray-500">Event Types</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default WebhookManagement;
